import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, Eye, ArrowRight, DollarSign } from "lucide-react";
import { jobStatus, badgeStyle } from "@/utils/szStatus";

const getJobTypeLabel = (type) => {
  const map = { WEDDINGS: "Wedding", CORPORATE: "Corporate", EVENTS: "Events", REAL_ESTATE: "Real Estate", OTHERS: "Other" };
  return map[type] || type || "";
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "$0";
  const num = parseFloat(amount);
  if (num >= 1000) return `$${(num / 1000).toFixed(1)}k`;
  return `$${num.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "TBD";
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, "0")} ${d.toLocaleString("en-US", { month: "short" })} ${d.getFullYear()}`;
};

const AVATAR_COLORS = ["#572B7C", "#78287C", "#28C0D0", "#0F766E", "#2563EB", "#15803D", "#D97706", "#0E7490"];
const initials = (n) => {
  const p = (n || "").trim().split(/\s+/);
  return (((p[0] || "")[0] || "") + ((p[1] || "")[0] || "")).toUpperCase() || "?";
};

const DashboardActionItems = ({
  pendingApprovals,
  pendingJobs,
  releasePaymentJobs,
  loading,
  onApproveUser,
  onViewJob,
  onEditJob,
  onApproveJob,
  onCancelJob,
  onViewUser,
}) => {
  const counts = useMemo(() => ({
    users: pendingApprovals?.length ?? 0,
    jobs: pendingJobs?.length ?? 0,
    payments: releasePaymentJobs?.length ?? 0,
  }), [pendingApprovals, pendingJobs, releasePaymentJobs]);

  const totalActions = counts.users + counts.jobs + counts.payments;

  // Auto-select tab with most items
  const fullestTab = useMemo(() => {
    if (counts.users >= counts.jobs && counts.users >= counts.payments) return "users";
    if (counts.jobs >= counts.payments) return "jobs";
    return "payments";
  }, [counts]);

  const [tab, setTab] = useState(fullestTab);
  useEffect(() => { setTab(fullestTab); }, [fullestTab]);

  if (loading) {
    return (
      <section className="sz-card mt-[18px] overflow-hidden">
        <div className="p-5">
          <div className="mb-4 h-10 w-72 animate-pulse rounded-lg bg-[#F1F3F7]" />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="mb-2 h-14 animate-pulse rounded-xl bg-[#F1F3F7]" />
          ))}
        </div>
      </section>
    );
  }

  // All caught up state
  if (totalActions === 0) {
    return (
      <section className="sz-card mt-[18px] flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[#DCFCE7] text-[#15803D]">
          <Check className="h-7 w-7" strokeWidth={2.4} />
        </div>
        <h3 className="text-[16px] font-bold text-[#0C1120]" style={{ fontFamily: "Poppins,sans-serif" }}>
          All Caught Up!
        </h3>
        <p className="mt-1 text-[13px] text-[#8A90A2]">No pending approvals, jobs, or payments to review.</p>
      </section>
    );
  }

  const tabs = [
    { id: "users", label: "Users", count: counts.users },
    { id: "jobs", label: "Jobs", count: counts.jobs },
    { id: "payments", label: "Payments", count: counts.payments },
  ];

  return (
    <section className="sz-card mt-[18px] overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3.5 px-5 pt-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-[30px] w-[30px] items-center justify-center rounded-[9px] bg-[#FEF3C7] text-[#B45309]">
            <AlertTriangle className="h-[17px] w-[17px]" strokeWidth={2.2} />
          </div>
          <h2 className="text-[16px] font-bold text-[#0C1120]" style={{ fontFamily: "Poppins,sans-serif" }}>
            Action Items
          </h2>
          <span
            className="rounded-full px-[9px] py-[2px] text-[12px] font-bold text-[#B45309]"
            style={{ fontFamily: "Poppins,sans-serif", background: "#FEF3C7" }}
          >
            {totalActions} pending
          </span>
        </div>
        <div className="flex gap-1 rounded-[11px] bg-[#F1F3F7] p-1">
          {tabs.map((t) => {
            const on = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className="flex items-center gap-[7px] rounded-lg px-3.5 py-[7px] text-[12.5px] font-semibold transition-all"
                style={{
                  color: on ? "#0C1120" : "#8A90A2",
                  background: on ? "#fff" : "transparent",
                  boxShadow: on ? "0 1px 3px rgba(12,17,32,.12)" : "none",
                }}
              >
                {t.label}
                <span
                  className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[10.5px] font-bold"
                  style={{
                    fontFamily: "Poppins,sans-serif",
                    background: on ? "#0C1120" : "#E0E3EB",
                    color: on ? "#fff" : "#8A90A2",
                  }}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Users */}
      {tab === "users" && (
        <div className="px-3 pb-3 pt-2">
          {(pendingApprovals?.slice(0, 5) || []).map((user, i) => (
            <div
              key={user.id ?? i}
              onClick={() => onViewUser?.(user)}
              className="flex cursor-pointer items-center gap-3.5 rounded-xl px-3 py-[11px] transition-colors hover:bg-[#F7F8FB]"
            >
              <div
                className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-full text-[13px] font-bold text-white"
                style={{ fontFamily: "Poppins,sans-serif", background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
              >
                {initials(user.full_name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold text-[#0C1120]">{user.full_name}</div>
                <div className="truncate text-[12px] text-[#8A90A2]">{user.email}</div>
              </div>
              {user.role && (
                <span className="flex-none rounded-full bg-[#EEF0F4] px-2.5 py-1 text-[11px] font-semibold text-[#2B3142]">
                  {user.role}
                </span>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onApproveUser?.(user.id); }}
                className="sz-btn sz-btn-primary flex-none !rounded-[9px] !px-[15px] !py-2 !text-[12.5px]"
              >
                <Check className="h-3.5 w-3.5" strokeWidth={2.4} />
                Approve
              </button>
            </div>
          ))}
          <Link
            to="/szadmin/users"
            className="flex items-center justify-center gap-1 py-[11px] text-[12.5px] font-semibold text-[#572B7C] no-underline"
          >
            View all {counts.users} pending users <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      {/* Jobs */}
      {tab === "jobs" && (
        <div className="px-3 pb-3 pt-2">
          {(pendingJobs?.slice(0, 4) || []).map((job, i) => {
            const st = jobStatus("PENDING_APPROVAL");
            return (
              <div key={job.id ?? i} className="flex items-center gap-3.5 rounded-xl p-3 transition-colors hover:bg-[#F7F8FB]">
                <span className="sz-badge sz-nodot flex-none" style={badgeStyle(st)}>{st.label}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px] font-semibold text-[#0C1120]">{job.title}</div>
                  <div className="mt-[2px] truncate text-[11.5px] text-[#8A90A2]">
                    {[
                      job.job_number || (job.id ? `JOB-${String(job.id).substring(0, 6).toUpperCase()}` : null),
                      job.job_type ? getJobTypeLabel(job.job_type) : null,
                      job.location || job.location_address,
                    ].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="flex-none text-right">
                  <div className="text-[14px] font-bold text-[#0C1120]" style={{ fontFamily: "Poppins,sans-serif" }}>
                    {formatCurrency(job.budget || job.total)}
                  </div>
                  {(job.owner?.name || job.owner_name) && (
                    <div className="text-[11px] text-[#8A90A2]">{job.owner?.name || job.owner_name}</div>
                  )}
                </div>
                <div className="flex flex-none gap-1.5">
                  <button
                    onClick={() => onViewJob?.(job)}
                    title="View"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E0E3EB] bg-white text-[#2B3142] transition-colors hover:border-[#28C0D0] hover:text-[#0C1120]"
                  >
                    <Eye className="h-[15px] w-[15px]" strokeWidth={2} />
                  </button>
                  <button
                    onClick={() => onApproveJob?.(job)}
                    className="sz-btn sz-btn-primary !h-8 !rounded-lg !px-3 !py-0 !text-[12px]"
                  >
                    <Check className="h-[13px] w-[13px]" strokeWidth={2.6} />
                    Approve
                  </button>
                </div>
              </div>
            );
          })}
          <Link
            to="/szadmin/job-approval"
            className="flex items-center justify-center gap-1 py-[11px] text-[12.5px] font-semibold text-[#572B7C] no-underline"
          >
            View all {counts.jobs} pending jobs <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      {/* Payments */}
      {tab === "payments" && (
        <div className="px-3 pb-3 pt-2">
          {(releasePaymentJobs?.slice(0, 4) || []).map((job, i) => (
            <div key={job.id ?? i} className="flex items-center gap-3.5 rounded-xl p-3 transition-colors hover:bg-[#F7F8FB]">
              <div className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] bg-[#DCFCE7] text-[#15803D]">
                <DollarSign className="h-[18px] w-[18px]" strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-semibold text-[#0C1120]">{job.title}</div>
                <div className="mt-[2px] truncate text-[11.5px] text-[#8A90A2]">
                  {[job.crew_name, job.date ? formatDate(job.date) : null].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="flex-none text-[15px] font-bold text-[#15803D]" style={{ fontFamily: "Poppins,sans-serif" }}>
                {formatCurrency(job.total_crew_payout || job.budget)}
              </div>
              <Link
                to="/szadmin/payment-release"
                className="sz-btn flex-none !rounded-[9px] !px-[15px] !py-2 !text-[12.5px] !text-white"
                style={{ background: "#15803D" }}
              >
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
                Release
              </Link>
            </div>
          ))}
          <Link
            to="/szadmin/payment-release"
            className="flex items-center justify-center gap-1 py-[11px] text-[12.5px] font-semibold text-[#572B7C] no-underline"
          >
            View all {counts.payments} pending payments <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </section>
  );
};

export default DashboardActionItems;
